import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, Platform, ActivityIndicator, StyleSheet } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import { GOOGLE_WEB_CLIENT_ID, WEB_SOCIAL_AUTH_ENABLED } from '../../config/socialAuth';
import { useSocialAuthRegion } from '../../hooks/useSocialAuthRegion';
import { shouldShowSocialAuth } from '../../utils/socialAuthGate';
import { safeImport } from '../../utils/staleBundle';
import { useI18n } from '../../i18n';
import { authErrorKey } from '../../screens/auth/authErrorCodes';
import { typographyTokens } from '../../styles/typographyTokens';
import { useTheme } from '../../context/ThemeContext';

type Props = {
  /** Surface auth errors to the host screen's error banner. */
  onError?: (message: string) => void;
  /** Optional hook after a successful sign-in (redirect is handled by the
   *  screen's isAuthenticated effect, so this is usually unnecessary). */
  /** CORE-513 — вызывающий решает, показать ли приветствие Искателя, поэтому
   *  ему нужно знать, новый это человек или уже существующий. Признак приходит
   *  с сервера (CORE-515), а не выводится из браузера. */
  onSuccess?: (result: { isNewUser: boolean }) => void;
  appearance?: 'default' | 'register';
};

/**
 * CORE-108 — "Continue with Google / Apple" row.
 *
 * Web-only for now (native needs the Expo auth modules + an EAS rebuild — see
 * socialAuthWeb.ts). Renders nothing on native, and nothing when the server
 * reports the providers as unconfigured (`/api/auth/providers`). Google renders
 * its own official button into a div; Apple is a styled button that runs the
 * popup flow in AuthContext.
 *
 * AMB-269 — Google/Apple sign-in is disabled for visitors in RU. Both
 * providers route through Google/Apple infra that is unreliable from
 * Russia, so we hide the whole social row there and leave email+password
 * as the only path. Region is resolved via the same GeoIP source as the
 * rest of the restricted-region UI (`/api/region`), with a browser-timezone
 * fallback for first paint.
 *
 * CORE-1574 — the row is switched off on web altogether
 * (`WEB_SOCIAL_AUTH_ENABLED`). The switch sits in a wrapper so that a hidden
 * row also skips the providers request and Google's GIS script.
 */
export const SocialAuthButtons: React.FC<Props> = (props) =>
  WEB_SOCIAL_AUTH_ENABLED ? <SocialAuthRow {...props} /> : null;

const SocialAuthRow: React.FC<Props> = ({ onError, onSuccess, appearance = 'default' }) => {
  const { loginWithGoogleToken, loginWithApple } = useAuth();
  const { t } = useI18n('auth');
  const { isDark } = useTheme();
  const registerAppearance = appearance === 'register';
  const registerText = isDark ? '#F7F4EF' : '#1C2228';
  const [providers, setProviders] = useState<{ google: boolean; apple: boolean } | null>(null);
  const [busy, setBusy] = useState<null | 'google' | 'apple'>(null);
  // GIS script failed to load (ad-blocker, offline). In register appearance we
  // hide the Google row instead of leaving a dead-looking button around.
  const [googleFailed, setGoogleFailed] = useState(false);
  // CORE-730 — region gate shared with the native variant (single source).
  const { restricted, regionResolved } = useSocialAuthRegion();
  const googleRef = useRef<any>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let alive = true;
    api.getAuthProviders()
      .then((p) => { if (alive) setProviders({ google: !!p.google, apple: !!p.apple }); })
      .catch(() => { if (alive) setProviders(null); });
    return () => { alive = false; };
  }, []);

  // Render Google's official button once we know the provider is enabled.
  useEffect(() => {
    if (Platform.OS !== 'web' || !providers?.google || !googleRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const { renderGoogleButton } = await safeImport(() => import('../../utils/socialAuthWeb'));
        await renderGoogleButton(
          googleRef.current,
          GOOGLE_WEB_CLIENT_ID,
          async (idToken: string) => {
            if (cancelled) return;
            setBusy('google');
            try {
              const result = await loginWithGoogleToken(idToken);
              onSuccess?.({ isNewUser: !!result?.isNewUser });
            } catch (e: any) {
              // CORE-538 — BE returns { code } on auth errors (CORE-515);
              // INVALID_SOCIAL_TOKEN gets localized copy instead of the raw message.
              const key = authErrorKey(e);
              onError?.(key ? t(key) : e?.message || 'Не удалось войти через Google');
            } finally {
              if (!cancelled) setBusy(null);
            }
          },
          // CORE-574 — the register card frames Google's official button in the
          // mockup's rectangular field style; the login screen keeps the pill.
          registerAppearance
            ? { shape: 'rectangular', text: 'signin_with', theme: isDark ? 'filled_black' : 'outline' }
            : undefined,
        );
      } catch (e: any) {
        if (registerAppearance) {
          setGoogleFailed(true);
        } else {
          onError?.(e?.message || 'Не удалось загрузить вход через Google');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [providers?.google, loginWithGoogleToken, onError, onSuccess, t, registerAppearance, isDark]);

  const handleApple = useCallback(async () => {
    setBusy('apple');
    try {
      const result = await loginWithApple();
      onSuccess?.({ isNewUser: !!result?.isNewUser });
    } catch (e: any) {
      // Popup dismissed / cancelled — not an error worth surfacing.
      const cancelled = e?.error === 'popup_closed_by_user' || /cancel|closed/i.test(e?.message || '');
      if (!cancelled) {
        const key = authErrorKey(e);
        onError?.(key ? t(key) : e?.message || 'Не удалось войти через Apple');
      }
    } finally {
      setBusy(null);
    }
  }, [loginWithApple, onError, onSuccess, t]);

  const hasProvider = !!providers && (providers.google || providers.apple);
  if (Platform.OS !== 'web' || !shouldShowSocialAuth({ restricted, regionResolved, hasProvider })) {
    return null;
  }
  // `hasProvider` already guarantees this; the explicit check narrows the type
  // for the JSX below (the gate helper is opaque to TS control-flow analysis).
  if (!providers) return null;

  return (
    <View style={[styles.wrap, registerAppearance && styles.registerWrap]}>
      <View style={[styles.dividerRow, registerAppearance && styles.registerDividerRow]}>
        <View style={[
          styles.divider,
          registerAppearance && styles.registerDivider,
          registerAppearance && isDark && styles.registerDividerDark,
        ]} />
        <Text style={[
          styles.dividerText,
          registerAppearance && styles.registerDividerText,
          registerAppearance && { color: isDark ? 'rgba(247, 244, 239, 0.68)' : 'rgba(31, 38, 45, 0.58)' },
        ]}>{t('register.or')}</Text>
        <View style={[
          styles.divider,
          registerAppearance && styles.registerDivider,
          registerAppearance && isDark && styles.registerDividerDark,
        ]} />
      </View>

      {providers.google && !googleFailed && (
        <View style={[
          styles.googleHost,
          registerAppearance && styles.registerGoogleHost,
          registerAppearance && isDark && styles.registerGoogleHostDark,
          providers.apple && { marginBottom: registerAppearance ? 10 : 12 },
        ]}>
          {Platform.OS === 'web' ? (
            // Google injects its official button into this element (web only).
            <div
              ref={googleRef}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            />
          ) : null}
        </View>
      )}

      {providers.apple && (
        <TouchableOpacity
          onPress={handleApple}
          disabled={!!busy}
          activeOpacity={0.85}
          style={[
            styles.appleBtn,
            registerAppearance && styles.registerAppleBtn,
            registerAppearance && isDark && styles.registerAppleBtnDark,
            !!busy && { opacity: 0.6 },
          ]}
          accessibilityRole="button"
          accessibilityLabel={t('register.signInWithApple')}
        >
          {busy === 'apple'
            ? <ActivityIndicator color={registerAppearance ? registerText : '#FFFFFF'} />
            : <Ionicons name="logo-apple" size={registerAppearance ? 20 : 18} color={registerAppearance ? registerText : '#FFFFFF'} />}
          <Text style={[
            styles.appleText,
            registerAppearance && styles.registerAppleText,
            registerAppearance && { color: registerText },
          ]}>{t('register.signInWithApple')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { width: '100%', marginTop: 16 },
  dividerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  divider: { flex: 1, height: 1, backgroundColor: 'rgba(15,20,25,0.08)' },
  dividerText: { marginHorizontal: 12, color: '#8A877F', ...typographyTokens.caption } as any,
  googleHost: { width: '100%', minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  appleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#000000',
  },
  appleText: { color: '#FFFFFF', ...typographyTokens.buttonSecondary } as any,
  registerWrap: {
    marginTop: 24,
  },
  registerDividerRow: {
    marginBottom: 14,
  },
  registerDivider: {
    backgroundColor: 'rgba(94, 83, 74, 0.22)',
  },
  registerDividerDark: {
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
  },
  registerDividerText: {
    ...typographyTokens.body,
  } as any,
  // CORE-574 — the mockup's field-style frame around Google's official
  // button. GIS only renders its own 40px button, so the frame supplies the
  // approved radius/border/background and the button sits centered inside.
  registerGoogleHost: {
    height: 48,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(119, 87, 55, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.38)',
    overflow: 'hidden',
    justifyContent: 'center',
  },
  registerGoogleHostDark: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderColor: 'rgba(255, 255, 255, 0.16)',
  },
  registerAppleBtn: {
    height: 48,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.38)',
    borderWidth: 1,
    borderColor: 'rgba(119, 87, 55, 0.18)',
  },
  registerAppleBtnDark: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderColor: 'rgba(255, 255, 255, 0.16)',
  },
  registerAppleText: {
    ...typographyTokens.buttonSecondary,
  } as any,
});
